import { CHUNK_HEIGHT, CHUNK_SIZE, CHUNK_VOLUME, SEA_LEVEL } from "../config";
import { Block } from "../engine/blocks";
import { blockIndex } from "../engine/chunk";
import { hash3, SimplexWorld } from "../engine/noise";
import { Biome, biomeDef, sampleColumn, type BiomeId } from "./biomes";
import { decorateChunk } from "./features";

export interface ChunkGenResult {
  cx: number;
  cz: number;
  blocks: Uint16Array;
  biomes: Uint8Array;
  heights: Uint8Array;
}

const noiseCache = new Map<number, SimplexWorld>();

export function noiseForSeed(seed: number): SimplexWorld {
  let n = noiseCache.get(seed);
  if (!n) {
    n = new SimplexWorld(seed);
    noiseCache.set(seed, n);
    if (noiseCache.size > 4) {
      const first = noiseCache.keys().next().value;
      if (first !== undefined) noiseCache.delete(first);
    }
  }
  return n;
}

function isCave(noise: SimplexWorld, wx: number, wy: number, wz: number, height: number): boolean {
  if (wy < 3) return false;
  const cover = height - wy;
  if (cover < 2) return false;

  const n1 = noise.simplex3(wx * 0.028, wy * 0.04, wz * 0.028);
  const n2 = noise.simplex3(wx * 0.028 + 80, wy * 0.038 + 12, wz * 0.028 - 40);
  const noodle = n1 * n1 + n2 * n2;

  // Rare surface mouths only — keep the topsoil intact otherwise.
  if (cover < 7) {
    return noodle < 0.008 && cover >= 2;
  }

  if (noodle < 0.038) return true;

  if (wy < 38 && cover >= 8) {
    const cheese = noise.simplex3(wx * 0.016 + 4, wy * 0.02, wz * 0.016);
    if (cheese > 0.72) return true;
  }
  return false;
}

function placeOre(
  blocks: Uint16Array,
  wx: number,
  wy: number,
  wz: number,
  lx: number,
  ly: number,
  lz: number,
  seed: number,
): void {
  if (wy < 1 || wy > 80) return;
  const i = blockIndex(lx, ly, lz);
  if (blocks[i] !== Block.STONE) return;
  const h = hash3(wx, wy, wz, seed);
  if (wy < 16 && h < 0.0016) {
    blocks[i] = Block.DIAMOND_ORE;
    return;
  }
  if (wy < 32 && h < 0.0045) {
    blocks[i] = Block.GOLD_ORE;
    return;
  }
  if (wy < 52 && h < 0.012) {
    blocks[i] = Block.IRON_ORE;
    return;
  }
  if (wy < 80 && h < 0.022) {
    blocks[i] = Block.COAL_ORE;
  }
}

export function generateChunkColumn(cx: number, cz: number, seed: number): ChunkGenResult {
  const noise = noiseForSeed(seed);
  const blocks = new Uint16Array(CHUNK_VOLUME);
  const biomes = new Uint8Array(CHUNK_SIZE * CHUNK_SIZE);
  const heights = new Uint8Array(CHUNK_SIZE * CHUNK_SIZE);
  const surfaces = new Uint8Array(CHUNK_SIZE * CHUNK_SIZE);

  const originX = cx * CHUNK_SIZE;
  const originZ = cz * CHUNK_SIZE;

  for (let lz = 0; lz < CHUNK_SIZE; lz++) {
    for (let lx = 0; lx < CHUNK_SIZE; lx++) {
      const wx = originX + lx;
      const wz = originZ + lz;
      const col = sampleColumn(noise, wx, wz, SEA_LEVEL);
      const def = biomeDef(col.biome);
      const ci = lx + lz * CHUNK_SIZE;
      biomes[ci] = col.biome;
      heights[ci] = col.height;

      let surface = def.surface;
      if (col.biome !== Biome.DESERT && col.biome !== Biome.BEACH && col.biome !== Biome.OCEAN) {
        if (col.height <= SEA_LEVEL + 1 && col.height >= SEA_LEVEL - 1) surface = Block.SAND;
      }
      if (col.biome === Biome.SNOW && col.height > SEA_LEVEL) surface = Block.SNOWY_GRASS;
      if (col.biome === Biome.PEAK) surface = Block.SNOW;
      if (col.height < SEA_LEVEL - 2 && (col.biome === Biome.OCEAN || col.biome === Biome.BEACH)) {
        surface = Block.SAND;
      }
      if (col.biome === Biome.SWAMP && col.height <= SEA_LEVEL + 1) {
        surface = hash3(wx, col.height, wz, seed) < 0.35 ? Block.CLAY : Block.GRASS;
      }
      surfaces[ci] = surface;

      const dirtDepth = col.biome === Biome.MOUNTAIN || col.biome === Biome.PEAK ? 2 : 4;

      for (let y = 0; y < CHUNK_HEIGHT; y++) {
        const i = blockIndex(lx, y, lz);
        if (y === 0) {
          blocks[i] = Block.BEDROCK;
          continue;
        }
        if (y > col.height) {
          if (y <= SEA_LEVEL) {
            blocks[i] =
              col.biome === Biome.SNOW || col.biome === Biome.PEAK
                ? y === SEA_LEVEL
                  ? Block.ICE
                  : Block.WATER
                : Block.WATER;
          }
          continue;
        }
        if (y === col.height) {
          blocks[i] = surface;
        } else if (y >= col.height - dirtDepth) {
          blocks[i] = def.fill === Block.STONE ? Block.STONE : def.fill;
          if (col.biome === Biome.DESERT) blocks[i] = Block.SAND;
          if (col.biome === Biome.BEACH || col.biome === Biome.OCEAN) blocks[i] = Block.SAND;
        } else {
          blocks[i] = Block.STONE;
        }
      }
    }
  }

  // caves + ores
  for (let lz = 0; lz < CHUNK_SIZE; lz++) {
    for (let lx = 0; lx < CHUNK_SIZE; lx++) {
      const wx = originX + lx;
      const wz = originZ + lz;
      const height = heights[lx + lz * CHUNK_SIZE]!;
      for (let y = 1; y < height; y++) {
        if (isCave(noise, wx, y, wz, height)) {
          const i = blockIndex(lx, y, lz);
          const cur = blocks[i]!;
          if (cur !== Block.BEDROCK && cur !== Block.WATER && cur !== Block.ICE) {
            blocks[i] = y <= SEA_LEVEL && height < SEA_LEVEL ? Block.WATER : Block.AIR;
          }
        } else {
          placeOre(blocks, wx, y, wz, lx, y, lz, seed);
        }
      }
    }
  }

  decorateChunk(cx, cz, seed, blocks, heights, biomes, (wx, wz) => {
    const col = sampleColumn(noise, wx, wz, SEA_LEVEL);
    const def = biomeDef(col.biome);
    let surface = def.surface;
    if (col.height <= SEA_LEVEL + 1 && col.height >= SEA_LEVEL - 1) {
      if (col.biome !== Biome.DESERT) surface = Block.SAND;
    }
    return { height: col.height, biome: col.biome, surface };
  });

  return { cx, cz, blocks, biomes, heights };
}

export function findSpawn(seed: number): { x: number; y: number; z: number } {
  const noise = noiseForSeed(seed);
  const tryAt = (x: number, z: number) => {
    const col = sampleColumn(noise, x, z, SEA_LEVEL);
    if (col.height > SEA_LEVEL + 1 && col.biome !== Biome.OCEAN && col.biome !== Biome.PEAK) {
      return { x: x + 0.5, y: col.height + 2, z: z + 0.5 };
    }
    return null;
  };
  const origin = tryAt(0, 0);
  if (origin) return origin;
  for (let r = 8; r <= 240; r += 8) {
    for (let a = 0; a < 12; a++) {
      const ang = (a / 12) * Math.PI * 2;
      const hit = tryAt(Math.round(Math.cos(ang) * r), Math.round(Math.sin(ang) * r));
      if (hit) return hit;
    }
  }
  return { x: 0.5, y: 90, z: 0.5 };
}

export type { BiomeId };
